import type { RebalancedOptimizerPortfolioDefinitionActionsState } from 'components/copilot/lab/definition/PortfolioDefinitionActions';
import {
  type IPortfolioDefinitionOptimizerInputQuery,
  ILeverageType,
  type IPrimaryConstrainedQuantity,
  type ISecondaryConstrainedQuantity,
  IConstraintFormulation,
  IRiskBudgetAllocation,
} from 'generated/graphql.tsx';
import type { PortfolioDefinitionWithAssetScreenerInputFields } from './PortfolioDefinitionWithAssetScreener.validation';
import type { PortfolioDefinitionWithAssetUniverseInputFields } from './PortfolioDefinitionWithAssetUniverse.validation';
import isNil from 'lodash/fp/isNil';
import { logWarnOnce } from 'components/log.utils';
import {
  type AssetLabelInput,
  isAssetLabelInput,
  type NotVerifiedAsset,
} from 'components/market/asset/AssetLabelService';
import { ConstraintType } from '../ConstraintTypeValues.validation';
import { RiskDistributionOption } from '../assumptionsAndOutlook/AssumptionsAndOutlook.validation';

const mapAsset = (
  benchmark: { id: string } | null | undefined,
  idToAsset: Map<string, NotVerifiedAsset>
): AssetLabelInput | null => {
  if (isNil(benchmark)) {
    return null;
  }

  const asset = idToAsset.get(benchmark.id);
  if (isNil(asset)) {
    logWarnOnce(`Asset ${benchmark.id} not found in supported assets by the optimizer. will be excluded`);
    return null;
  }

  if (isAssetLabelInput(asset)) {
    return asset;
  }

  return null;
};

const mapConstraintValue = (
  constraint: { lowerBound: number; upperBound: number } | null | undefined,
  constraintFormulation: IConstraintFormulation
): { value: string | null; min: string | null; max: string | null } => {
  if (isNil(constraint)) {
    return {
      value: null,
      min: null,
      max: null,
    };
  }

  const multiplier = constraintFormulation === IConstraintFormulation.Percentage ? 100 : 1;

  return {
    value:
      constraint.lowerBound === constraint.upperBound
        ? ((multiplier * constraint.lowerBound).toString() ?? null)
        : null,
    min: (multiplier * constraint.lowerBound).toString() ?? null,
    max: (multiplier * constraint.upperBound).toString() ?? null,
  };
};

const mapConstraintType = (
  constraint?: { lowerBound: number; upperBound: number } | null
): ConstraintType.Between | ConstraintType.Equal => {
  if (constraint?.lowerBound === constraint?.upperBound) {
    return ConstraintType.Equal;
  }

  return ConstraintType.Between;
};

const mapPrimaryConstraint = (
  constraint:
    | {
        constrainedQuantity: IPrimaryConstrainedQuantity;
        constraint: { lowerBound: number; upperBound: number };
        riskMetric?: { metricLabel: string; benchmark?: { id: string } | null } | null;
      }
    | null
    | undefined,
  idToAsset: Map<string, NotVerifiedAsset>
) =>
  constraint
    ? {
        constrainedQuantity: constraint.constrainedQuantity,
        constraintType: mapConstraintType(constraint.constraint),
        constraintValue: mapConstraintValue(constraint.constraint, IConstraintFormulation.Percentage),
        riskMetric: {
          name: constraint.riskMetric?.metricLabel ?? '',
          benchmark: mapAsset(constraint.riskMetric?.benchmark, idToAsset),
        },
      }
    : null;

const mapSecondaryConstraint = (
  constraint:
    | {
        constrainedQuantity: ISecondaryConstrainedQuantity;
        constraint: { lowerBound: number; upperBound: number };
        riskMetric?: { metricLabel: string; benchmark?: { id: string } | null } | null;
      }
    | null
    | undefined,
  idToAsset: Map<string, NotVerifiedAsset>
) =>
  constraint
    ? {
        constrainedQuantity: constraint.constrainedQuantity,
        constraintType: mapConstraintType(constraint.constraint),
        constraintValue: mapConstraintValue(constraint.constraint, IConstraintFormulation.Percentage),
        benchmark: mapAsset(constraint.riskMetric?.benchmark, idToAsset),
      }
    : null;

const mapStateToDefaultValues = (
  actionState: RebalancedOptimizerPortfolioDefinitionActionsState,
  assets: IPortfolioDefinitionOptimizerInputQuery['assets']
): Omit<PortfolioDefinitionWithAssetScreenerInputFields, 'filters' | 'portfolioAmount' | 'useUnderlying'> | null => {
  const { name, description, optimizerRule } = actionState;
  const optimizationInput = optimizerRule.optimizationInput;
  const idToAsset = new Map(assets.feature.map((asset) => [asset.id, asset]));
  const idToGroup = new Map(
    [...assets.assetGroups.genieGroups, ...assets.assetGroups.userGroups].map((group) => [group.id, group])
  );

  return {
    name,
    description,
    objectives: optimizationInput.objectives.map((obj) => ({
      type: obj.objectiveType,
      riskMetric: obj.risk ?? '',
      benchmark: mapAsset(obj.benchmark, idToAsset),
    })),
    constraints: [
      ...optimizationInput.assets.map((a) => ({
        item: { type: 'asset' as const, asset: mapAsset(a.asset, idToAsset) },
        constraintValue: mapConstraintValue(a.constraint, IConstraintFormulation.Percentage),
        constraintType: mapConstraintType(a.constraint),
      })),
      ...optimizationInput.groupConstraints.flatMap((gc) =>
        gc.allocationConstraints.map((ac) => ({
          item: { type: 'group' as const, group: idToGroup.get(gc.group.id) ?? null },
          constrainedQuantity: ac.constrainedQuantity,
          constraintValue: mapConstraintValue(ac.constraint, IConstraintFormulation.Percentage),
          constraintType: mapConstraintType(ac.constraint),
        }))
      ),
    ],
    portfolioConstraints: {
      primaryConstraint: mapPrimaryConstraint(optimizationInput.portfolioConstraints?.primary, idToAsset),
      secondaryConstraint: mapSecondaryConstraint(optimizationInput.portfolioConstraints?.secondary, idToAsset),
    },
    allowShortAndLeverage: optimizationInput.leverageType === ILeverageType.LongShort,
    returnsForecast: optimizationInput.returnMeasureName,
    constraintType: optimizationInput.constraintFormulation,
    multifactor: {
      factor: isNil(optimizationInput.multifactor) ? null : optimizationInput.multifactor.factor,
      minNumberOfFactors: isNil(optimizationInput.multifactor)
        ? null
        : optimizationInput.multifactor.minNumberOfFactors.toString(),
      useAbsoluteScores: isNil(optimizationInput.multifactor) ? true : optimizationInput.multifactor.useAbsoluteScores,
    },
    riskBudgetAllocation:
      optimizationInput.riskBudgetAllocation === IRiskBudgetAllocation.Multifactor
        ? RiskDistributionOption.MultifactorScore
        : RiskDistributionOption.UserSupplied,
  };
};

export function mapStateToDefaultValuesForScreener(
  actionState: RebalancedOptimizerPortfolioDefinitionActionsState | undefined,
  assets: IPortfolioDefinitionOptimizerInputQuery['assets']
): Partial<PortfolioDefinitionWithAssetScreenerInputFields> | null {
  if (isNil(actionState) || isNil(actionState.optimizerRule)) {
    return null;
  }

  const defaultValues = mapStateToDefaultValues(actionState, assets);
  return {
    ...defaultValues,
    filters: actionState.optimizerRule.filters,
  };
}

export function mapStateToDefaultValuesForUniverse(
  actionState: RebalancedOptimizerPortfolioDefinitionActionsState | undefined,
  assets: IPortfolioDefinitionOptimizerInputQuery['assets']
): Partial<PortfolioDefinitionWithAssetUniverseInputFields> | null {
  if (isNil(actionState) || isNil(actionState.optimizerRule)) {
    return null;
  }

  const defaultValues = mapStateToDefaultValues(actionState, assets);
  const idToAsset = new Map(assets.feature.map((asset) => [asset.id, asset]));
  const mappedUniverse = actionState.optimizerRule.assetIds.map((id) => idToAsset.get(id));
  return {
    ...defaultValues,
    universe: mappedUniverse.filter(
      (asset): asset is NonNullable<(typeof mappedUniverse)[number]> & { label: string } => {
        if (isNil(asset)) {
          logWarnOnce(`Asset ${asset} not found in supported assets by the optimizer. will be excluded`);
          return false;
        }

        if (isNil(asset.label)) {
          logWarnOnce(`Asset ${asset} has no label. will be excluded`);
          return false;
        }

        return true;
      }
    ),
  };
}
